"use client";

import { useState, useEffect, useRef } from "react";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectTrigger, SelectValue } from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { Badge } from "../components/ui/badge";
import { Separator } from "../components/ui/separator";
import {
  Type,
  Mail,
  Phone,
  Calendar,
  Hash,
  ToggleLeft,
  List,
  FileText,
  Star,
  Trash2,
  GripVertical,
  Plus,
} from "lucide-react";
import PropTypes from "prop-types";
import "./form-builder.css";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { Snackbar, Alert } from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import Backdrop from "@mui/material/Backdrop";
const fieldTypes = [
  { type: "text", label: "Text Input", icon: Type },
  { type: "email", label: "Email", icon: Mail },
  { type: "phone", label: "Phone", icon: Phone },
  { type: "number", label: "Number", icon: Hash },
  { type: "date", label: "Date", icon: Calendar },
  { type: "textarea", label: "Text Area", icon: FileText },
  { type: "select", label: "Dropdown", icon: List },
  { type: "radio", label: "Radio Buttons", icon: ToggleLeft },
  { type: "checkbox", label: "Checkboxes", icon: ToggleLeft },
  { type: "rating", label: "Rating", icon: Star },
];

export function FormBuilder({ onSave, editingForm, onCancelEdit }) {
  console.log("Editing Form:", editingForm);
  const [formTitle, setFormTitle] = useState("");
  const [formDescription, setFormDescription] = useState("");
  const [fields, setFields] = useState([]);
  const [selectedField, setSelectedField] = useState(null);
  const [loading, setLoading] = useState(false);

  const [state, setState] = useState({
    open: false,
    severity: "success",
    vertical: "top",
    horizontal: "right",
    message: "",
  });

  const me = async () => {
    const response = await axios({
      method: "post",
      url: `${process.env.REACT_APP_API_URL}/auth/me`,
      headers: {
        "Content-Type": "application/json",
      },
      withCredentials: true,
    });
    // console.log("🚀 ~ fetchUsers ~ response:", response);
    return response.data;
  };

  const handleClose = () => setState({ ...state, open: false });

  const {
    data: meData,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ["me"], // Unique key for the query
    queryFn: me, // The async function to run
    retry: false,
  });

  useEffect(() => {
    if (editingForm) {
      setFormTitle(editingForm.title);
      setFormDescription(editingForm.description || "");
      setFields(editingForm.fields || []);
      setSelectedField(null);
    }
  }, [editingForm]);

  const addField = (type) => {
    const newField = {
      id: `field_${Date.now()}`,
      type,
      label: `${fieldTypes.find((f) => f.type === type)?.label} Field`,
      required: false,
      placeholder: "",
      options:
        type === "select" || type === "radio" || type === "checkbox"
          ? ["Option 1", "Option 2"]
          : undefined,
    };
    setFields([...fields, newField]);
    setSelectedField(newField);
  };

  const updateField = (id, updates) => {
    setFields(fields.map((field) => (field.id === id ? { ...field, ...updates } : field)));
    if (selectedField?.id === id) {
      setSelectedField({ ...selectedField, ...updates });
    }
  };

  const removeField = (id) => {
    setFields(fields.filter((field) => field.id !== id));
    if (selectedField?.id === id) {
      setSelectedField(null);
    }
  };

  const saveForm = async () => {
    if (!formTitle.trim()) {
      alert("Please enter a form title");
      return;
    }

    const updatedFields = [...fields];

    const form = {
      id: editingForm ? editingForm.id : `form_${Date.now()}`,
      title: formTitle,
      description: formDescription,
      fields: updatedFields,
      createdAt: editingForm ? editingForm.createdAt : new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      ...(editingForm
        ? { updated_by: meData?.user_id }
        : { created_by: meData?.user_id, status: 2 }),
    };

    try {
      setLoading(true);
      let response;
      if (editingForm) {
        response = await axios.put(
          `${process.env.REACT_APP_API_URL}/forms/update/${editingForm.id}`,
          form,
          {
            headers: { "Content-Type": "application/json" },
            withCredentials: true,
          }
        );
      } else {
        response = await axios.post(`${process.env.REACT_APP_API_URL}/forms/add`, form, {
          headers: { "Content-Type": "application/json" },
          withCredentials: true,
        });
      }

      if (response.data?.status === 200) {
        setState((prev) => ({
          ...prev,
          open: true,
          severity: "success",
          message:
            response.data.message ||
            (editingForm ? "Form updated successfully!" : "Form saved successfully!"),
        }));

        setTimeout(() => {
          if (onSave) {
            onSave();
          }
        }, 1500);

        if (!editingForm) {
          setFormTitle("");
          setFormDescription("");
          setFields([]);
          setSelectedField(null);
        }
      }
    } catch (error) {
      console.error("❌ Error saving form:", error);

      if (error.response) {
        setState((prev) => ({
          ...prev,
          open: true,
          severity: "error",
          message: error.response.data.message || "Failed to save form",
        }));
      } else {
        setState((prev) => ({
          ...prev,
          open: true,
          severity: "error",
          message: "Server error. Please try again.",
        }));
      }
    } finally {
      setLoading(false);
    }
  };

  const cancelEdit = () => {
    if (onCancelEdit) {
      onCancelEdit();
    }
    // Reset to original values
    if (editingForm) {
      setFormTitle(editingForm.title);
      setFormDescription(editingForm.description || "");
      setFields(editingForm.fields || []);
      setSelectedField(null);
    }
  };

  const propertiesRef = useRef(null);
  return (
    <div className="form-builder">
      <Backdrop sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
        <CircularProgress color="inherit" />
      </Backdrop>
      <Snackbar
        open={state.open}
        autoHideDuration={1500}
        onClose={handleClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert onClose={handleClose} severity={state.severity} variant="filled">
          {state.message}
        </Alert>
      </Snackbar>
      {/* Form Settings */}
      <Card className="form-card">
        <CardHeader className="form-card-header">
          <CardTitle className="form-card-title">
            <Plus className="w-5 h-5" />
            {editingForm ? "Edit Form" : "Form Settings"} {/* Dynamic title */}
          </CardTitle>
          <CardDescription className="form-card-desc">
            {editingForm ? "Modify your existing form" : "Configure your form details"}{" "}
            {/* Dynamic description */}
          </CardDescription>
        </CardHeader>
        <CardContent className="form-card-content">
          <div>
            <Label htmlFor="title" className="form-label">
              Form Title
            </Label>
            <Input
              id="title"
              className="form-input"
              value={formTitle}
              onChange={(e) => setFormTitle(e.target.value)}
              placeholder="Enter form title"
            />
          </div>
          <div>
            <Label htmlFor="description" className="form-label">
              Description
            </Label>
            <Textarea
              id="description"
              className="form-textarea"
              value={formDescription}
              onChange={(e) => setFormDescription(e.target.value)}
              placeholder="Enter form description"
              rows={3}
            />
          </div>

          <Separator />

          <div>
            <Label className="form-label">Add Field Types</Label>
            <div className="field-type-grid">
              {fieldTypes.map((fieldType) => {
                const Icon = fieldType.icon;
                return (
                  <Button
                    key={fieldType.type}
                    variant="outline"
                    size="sm"
                    onClick={() => addField(fieldType.type)}
                    className="field-type-btn"
                  >
                    <Icon className="w-4 h-4" />
                    <span>{fieldType.label}</span>
                  </Button>
                );
              })}
            </div>
          </div>

          <Separator />

          <div className="form-actions">
            {" "}
            {/* Added button container */}
            <Button
              onClick={saveForm}
              className="form-btn form-btn-primary"
              disabled={!formTitle.trim() || fields.length === 0}
            >
              {editingForm ? "Update Form" : "Save Form"} {/* Dynamic button text */}
            </Button>
            {editingForm &&
              onCancelEdit && ( // Added cancel button for edit mode
                <Button className="form-btn form-btn-outline" onClick={cancelEdit}>
                  Cancel
                </Button>
              )}
          </div>
        </CardContent>
      </Card>

      {/* Form Preview */}
      <Card className="form-card">
        <CardHeader className="form-card-header">
          <CardTitle className="form-card-title">Form Preview</CardTitle>
          <CardDescription className="form-card-desc">See how your form will look</CardDescription>
        </CardHeader>

        <CardContent className="form-card-content">
          <div>
            {formTitle && (
              <div>
                <h3 className="text-xl font-semibold">{formTitle}</h3>
                {formDescription && <p className="form-card-desc">{formDescription}</p>}
              </div>
            )}

            {/* Enable Drag & Drop */}
            <DragDropContext
              onDragEnd={(result) => {
                if (!result.destination) return;
                const updated = Array.from(fields);
                const [moved] = updated.splice(result.source.index, 1);
                updated.splice(result.destination.index, 0, moved);
                setFields(updated);
              }}
            >
              <Droppable droppableId="form-fields">
                {(provided) => (
                  <div ref={provided.innerRef} {...provided.droppableProps}>
                    {fields.map((field, index) => (
                      <Draggable key={field.id} draggableId={field.id} index={index}>
                        {(provided, snapshot) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                            className={`preview-field ${selectedField?.id === field.id ? "active" : ""
                              }`}
                            onClick={() => {
                              setSelectedField(field);
                              setTimeout(() => {
                                propertiesRef.current?.scrollIntoView({
                                  behavior: "smooth",
                                  block: "start",
                                });
                              }, 100);
                            }}
                            style={{
                              ...provided.draggableProps.style,
                              cursor: "pointer",
                              marginBottom: "0.5rem",
                              background: snapshot.isDragging ? "#f9fafb" : "transparent",
                            }}
                          >
                            {/* Header row: left = drag + label + badge, right = delete */}
                            <div
                              className="preview-header"
                              style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                marginBottom: "0.5rem",
                              }}
                            >
                              {/* Left group */}
                              <div
                                className="preview-left"
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "0.6rem",
                                }}
                              >
                                <GripVertical
                                  className="drag-icon"
                                  style={{
                                    width: 18,
                                    height: 18,
                                    color: "#9CA3AF",
                                    flex: "0 0 auto",
                                  }}
                                />
                                <span
                                  className="field-label-text"
                                  style={{
                                    fontWeight: 600,
                                    color: "#0f172a",
                                    lineHeight: 1,
                                    display: "inline-block",
                                  }}
                                >
                                  {field.label}
                                </span>

                                {field.required && (
                                  <span
                                    className="field-badge"
                                    style={{
                                      marginLeft: 8,
                                      background: "#f1f5f9",
                                      color: "#111827",
                                      fontSize: "0.75rem",
                                      padding: "0.18rem 0.45rem",
                                      borderRadius: 6,
                                      border: "1px solid #e6e9ee",
                                      display: "inline-flex",
                                      alignItems: "center",
                                      height: 22,
                                    }}
                                  >
                                    Required
                                  </span>
                                )}
                              </div>

                              {/* Delete button (right side) */}
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  removeField(field.id);
                                }}
                                className="delete-btn"
                                aria-label={`Delete ${field.label}`}
                                style={{
                                  background: "transparent",
                                  border: "none",
                                  padding: 6,
                                  borderRadius: 6,
                                  cursor: "pointer",
                                  display: "inline-flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                }}
                              >
                                <Trash2 style={{ width: 18, height: 18, color: "#374151" }} />
                              </button>
                            </div>

                            {/* Field preview box (input / textarea / options) */}
                            <div className="preview-input-wrapper" style={{ marginTop: 4 }}>
                              {field.type === "textarea" ? (
                                <Textarea
                                  placeholder={field.placeholder}
                                  disabled
                                  style={{
                                    minWidth: "140px", // optional safeguard
                                    height: "30px",
                                    borderRadius: "8px",
                                    fontSize: "14px",
                                  }}
                                />
                              ) : field.type === "select" ? (
                                <Select disabled>
                                  <SelectTrigger
                                    style={{
                                      width: "200px",
                                      height: "30px",
                                      fontSize: "14px",
                                      borderRadius: "8px",
                                    }}
                                  >
                                    <SelectValue
                                      placeholder={field.placeholder || "Select an option"}
                                    />
                                  </SelectTrigger>
                                </Select>
                              ) : field.type === "radio" || field.type === "checkbox" ? (
                                <div
                                  style={{
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: 8,
                                  }}
                                >
                                  {field.options?.map((option, index) => (
                                    <div
                                      key={index}
                                      style={{
                                        display: "flex",
                                        gap: 8,
                                        alignItems: "center",
                                      }}
                                    >
                                      <input
                                        type={field.type}
                                        disabled
                                        style={{ width: 16, height: 16 }}
                                      />
                                      <span style={{ fontSize: 14 }}>{option}</span>
                                    </div>
                                  ))}
                                </div>
                              ) : field.type === "rating" ? (
                                <div style={{ display: "flex", gap: 6 }}>
                                  {[1, 2, 3, 4, 5].map((s) => (
                                    <Star
                                      key={s}
                                      style={{
                                        width: 18,
                                        height: 18,
                                        color: "#e6e9ee",
                                      }}
                                    />
                                  ))}
                                </div>
                              ) : (
                                <Input
                                  type={field.type}
                                  placeholder={field.placeholder}
                                  disabled
                                  style={{
                                    height: 44,
                                    minWidth: 190,
                                    borderRadius: 8,
                                    background: "#f8fafc",
                                    border: "1px solid #eef2f6",
                                    padding: "0 12px",
                                  }}
                                />
                              )}
                            </div>
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>

            {fields.length === 0 && (
              <div className="empty-state">
                <FileText />
                <p>Add fields to see your form preview</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Field Properties */}
      <Card className="form-card" ref={propertiesRef}>
        <CardHeader className="form-card-header">
          <CardTitle className="form-card-title">Field Properties</CardTitle>
          <CardDescription className="form-card-desc">
            {selectedField
              ? "Configure the selected field"
              : "Select a field to edit its properties"}
          </CardDescription>
        </CardHeader>
        <CardContent className="form-card-content">
          {selectedField ? (
            <div className="field-properties">
              <div className="form-group">
                <Label htmlFor="field-label" className="form-label">
                  Field Label
                </Label>
                <Input
                  id="field-label"
                  className="form-input"
                  value={selectedField.label}
                  onChange={(e) => updateField(selectedField.id, { label: e.target.value })}
                />
              </div>

              <div className="form-group">
                <Label htmlFor="field-placeholder" className="form-label">
                  Placeholder
                </Label>
                <Input
                  id="field-placeholder"
                  className="form-input"
                  value={selectedField.placeholder || ""}
                  onChange={(e) => updateField(selectedField.id, { placeholder: e.target.value })}
                />
              </div>

              <div className="form-group form-toggle">
                <Label htmlFor="field-required" className="form-label">
                  Required Field
                </Label>
                <Switch
                  id="field-required"
                  checked={selectedField.required}
                  onCheckedChange={(checked) =>
                    updateField(selectedField.id, { required: checked })
                  }
                />
              </div>

              {(selectedField.type === "select" ||
                selectedField.type === "radio" ||
                selectedField.type === "checkbox") && (
                  <div className="form-group" style={{ marginTop: "12px" }}>
                    <Label className="form-label">Options</Label>
                    <div className="field-options">
                      {selectedField.options?.map((option, index) => (
                        <div key={index} className="field-option">
                          <Input
                            className="form-input"
                            value={option}
                            onChange={(e) => {
                              const newOptions = [...(selectedField.options || [])];
                              newOptions[index] = e.target.value;
                              updateField(selectedField.id, { options: newOptions });
                            }}
                          />
                          <Button
                            className="delete-btn"
                            variant="outline"
                            onClick={() => {
                              const newOptions = selectedField.options?.filter((_, i) => i !== index);
                              updateField(selectedField.id, { options: newOptions });
                            }}
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      ))}
                      <Button
                        className="add-option-btn"
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          const newOptions = [
                            ...(selectedField.options || []),
                            `Option ${(selectedField.options?.length || 0) + 1}`,
                          ];
                          updateField(selectedField.id, { options: newOptions });
                        }}
                      >
                        <Plus className="w-3 h-3 mr-2" />
                        Add Option
                      </Button>
                    </div>
                  </div>
                )}

              {(selectedField.type === "text" || selectedField.type === "textarea") && (
                <div className="form-group">
                  <div>
                    <Label htmlFor="min-length" className="form-label">
                      Minimum Length
                    </Label>
                    <Input
                      id="min-length"
                      type="number"
                      className="form-input"
                      value={selectedField.validation?.minLength || ""}
                      onChange={(e) =>
                        updateField(selectedField.id, {
                          validation: {
                            ...selectedField.validation,
                            minLength: e.target.value ? Number.parseInt(e.target.value) : undefined,
                          },
                        })
                      }
                    />
                  </div>
                  <div>
                    <Label htmlFor="max-length" className="form-label">
                      Maximum Length
                    </Label>
                    <Input
                      id="max-length"
                      type="number"
                      className="form-input"
                      value={selectedField.validation?.maxLength || ""}
                      onChange={(e) =>
                        updateField(selectedField.id, {
                          validation: {
                            ...selectedField.validation,
                            maxLength: e.target.value ? Number.parseInt(e.target.value) : undefined,
                          },
                        })
                      }
                    />
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="empty-state">
              <GripVertical className="w-12 h-12 mx-auto mb-4 text-slate-300" />
              <p>Select a field from the preview to edit its properties</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

FormBuilder.propTypes = {
  onSave: PropTypes.func.isRequired, // callback when saving a form
  editingForm: PropTypes.shape({
    id: PropTypes.string,
    title: PropTypes.string.isRequired,
    description: PropTypes.string,
    fields: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        type: PropTypes.string.isRequired,
        label: PropTypes.string.isRequired,
        required: PropTypes.bool,
        placeholder: PropTypes.string,
        options: PropTypes.arrayOf(PropTypes.string),
        validation: PropTypes.shape({
          minLength: PropTypes.number,
          maxLength: PropTypes.number,
        }),
      })
    ),
    createdAt: PropTypes.string,
    updatedAt: PropTypes.string,
    submissions: PropTypes.array,
  }),
  onCancelEdit: PropTypes.func, // optional
};
